import { Card, CardContent, Typography, Stack, Box } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ExpirationTimelineEntry } from '../types';
import { useTranslation } from 'react-i18next';

interface ExpirationChartProps {
  data: ExpirationTimelineEntry[];
  loading?: boolean;
}

// Cores padrão do MUI
const getBarColor = (count: number) => {
  if (count >= 4) return '#d32f2f'; // error.main
  if (count >= 2) return '#ed6c02'; // warning.main
  return '#1976d2'; // primary.main
};

export default function ExpirationChart({ data, loading }: ExpirationChartProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card sx={{ minWidth: 200, height: '100%' }}>
        <CardContent>
          <Typography color="text.secondary">{t('common.loading', 'Loading...')}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Format data for the chart
  const chartData = data.map(entry => {
    const date = new Date(entry.week);
    return {
      week: date.toLocaleDateString(t('meta.languageCode', undefined), { month: 'short', day: 'numeric' }),
      count: entry.count,
      fullDate: entry.week,
    };
  });

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {t('dashboard.chart.title', 'Expiration Timeline')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.chart.subtitle', 'License expirations over the next 90 days (by week)')}
            </Typography>
          </Box>

          {chartData.length === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography color="text.secondary">
                {t('dashboard.chart.noData', 'No upcoming expirations in the next 90 days')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ed6c02" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ed6c02" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 11, fill: '#666' }}
                    tickLine={false}
                    axisLine={false}
                    dy={10}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#666' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ stroke: '#ed6c02', strokeWidth: 1, strokeDasharray: '4 4' }}
                    formatter={(value: any) => [value, t('dashboard.chart.expirations', 'Expirations')]}
                    contentStyle={{
                      borderRadius: 8,
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#ed6c02"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Legend */}

        </Stack>
      </CardContent>
    </Card>
  );
}
